import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Truck, ArrowRight, Check, MapPin, ShieldCheck, FileSearch, Gauge, Wrench,
  ClipboardCheck, Activity, Smartphone, CalendarClock, ScrollText, AlertTriangle, Loader2,
} from "lucide-react";

const BADGES = ["RSA compliant", "DVSA compliant", "EU compliant", "Paperless by design", "Earned Recognition ready", "GDPR & DPA aligned"];

const WALKAROUND = ["Tyres, wheels & brakes", "Lights & indicators", "MOT & test certificate", "Operator licence disc", "Tachograph & driver hours"];

const RISKS = [
  "Documents expire without warning",
  "Defects reported on paper, never actioned",
  "Driver hours breached without anyone noticing",
  "No single audit trail to show an inspector",
];

const FEATURES = [
  { icon: FileSearch, title: "AI document checking", text: "Upload a document and HaulCheck verifies it's valid, current and correctly filed." },
  { icon: Gauge, title: "Tacho Analyst", text: "Automated tachograph analysis that flags infringements the moment they happen." },
  { icon: Wrench, title: "MOT & recall tracking", text: "Every test, deadline and manufacturer recall tracked automatically." },
  { icon: ClipboardCheck, title: "Defects & walkarounds", text: "Daily checks, fully paperless, with photo evidence on every defect." },
  { icon: Activity, title: "AI risk scoring", text: "Every driver and vehicle scored for risk, so you know where to look first." },
  { icon: Smartphone, title: "Driver app", text: "Checks, defects and photos logged straight from a driver's phone." },
  { icon: CalendarClock, title: "Smart reminders & calendar", text: "Every compliance date in one calendar, with the right reminder at the right time." },
  { icon: ScrollText, title: "Built-in audit trail", text: "Every check and document logged, ready to hand to an inspector." },
];

const STATS = [
  { value: "12,000+", label: "Documents checked" },
  { value: "350+", label: "Fleets kept compliant" },
  { value: "18,000+", label: "Defects logged & actioned" },
  { value: "100%", label: "Audit-ready records" },
];

const PLAN_PERKS = [
  "30-day free trial — no card required",
  "10 vehicles included · unlimited trailers & drivers",
  "More than 10 vehicles? Contact us",
  "Unlimited documents, defects & audit PDFs",
  "UK (DVSA), Ireland (RSA) & EU formats",
  "Driver phone app included · cancel anytime, no contract",
];

const FLEET_SIZES = ["1-5 vehicles", "6-15 vehicles", "16-50 vehicles", "50+ vehicles"];

const HERO_IMAGES = [
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_transport-verify-3/artifacts/27rkzz8y_Pair%20580%20-%20620.webp", alt: "HGV fleet" },
  { src: "https://images.pexels.com/photos/38559056/pexels-photo-38559056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", alt: "HGV on the motorway" },
  { src: "https://images.unsplash.com/photo-1768400554801-2002b63e0591?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Delivery van" },
];

export default function Landing() {
  const { user } = useAuth();
  const primary = user ? "/dashboard" : "/trial";
  const [demo, setDemo] = useState({ name: "", company: "", email: "", phone: "", fleet_size: "" });
  const [sending, setSending] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 4500);
    return () => clearInterval(t);
  }, []);

  const submitDemo = async (e) => {
    e.preventDefault();
    if (!demo.name || !demo.email) { toast.error("Please add your name and email"); return; }
    setSending(true);
    try {
      await api.post("/contact", {
        name: demo.name,
        email: demo.email,
        subject: "Demo request",
        type: "demo",
        company: demo.company,
        phone: demo.phone,
        fleet_size: demo.fleet_size,
        message: `Demo request from ${demo.name} (${demo.company || "company not given"}).\nPhone: ${demo.phone || "—"}\nApprox fleet size: ${demo.fleet_size || "—"}`,
      });
      toast.success("Thanks — we'll be in touch soon");
      setDemo({ name: "", company: "", email: "", phone: "", fleet_size: "" });
    } catch { toast.error("Could not send your request — please try again"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="landing-page" className="bg-white text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Truck size={24} />
            <span className="font-heading font-black text-lg tracking-tight">HAULCHECK</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-slate-300">
            <a href="#platform" className="hover:text-white">Platform</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <Link to="/about" className="hover:text-white">About</Link>
            <a href="#demo" className="hover:text-white">Request a demo</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to={user ? "/dashboard" : "/login"} data-testid="landing-signin" className="text-sm font-semibold text-slate-200 hover:text-white">
              {user ? "Back to app" : "Sign in"}
            </Link>
            <Link to={primary} data-testid="landing-start-trial-header"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 text-white px-4 py-2 text-sm font-bold hover:bg-emerald-400 transition-colors">
              {user ? "Dashboard" : "Start free trial"}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-emerald-500/5 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-semibold animate-in-up">RSA, DVSA &amp; EU compliance, automated</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-5 leading-[1.03] animate-in-up">
              Keep your fleet <span className="italic text-emerald-400">compliant</span>, not just busy.
            </h1>
            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl animate-in-up">
              Haulcheck brings documents, vehicles, drivers and defects into one paperless system — with AI checking your paperwork and flagging risk before an inspector does.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 animate-in-up">
              <Link to={primary} data-testid="landing-start-trial-hero"
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-white px-6 py-3.5 font-bold hover:bg-emerald-400 transition-colors">
                {user ? "Go to dashboard" : "Start your free trial"} <ArrowRight size={18} />
              </Link>
              <Link to="/platform" data-testid="landing-see-platform" className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-6 py-3.5 font-semibold text-slate-200 hover:border-slate-500 transition-colors">
                See the platform
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5"><Check size={14} className="text-emerald-400" /> No card required · Built for small and medium operators · One plan, everything included</p>
            <Link to="/demo" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400 hover:text-emerald-300">
              Explore a live demo — no signup <ArrowRight size={15} />
            </Link>
          </div>

          {/* Product mockup */}
          <div className="animate-in-up relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 h-72 sm:h-80 lg:h-[34rem]">
              {HERO_IMAGES.map((im, i) => (
                <img key={im.src} src={im.src} alt={im.alt} loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === heroIdx ? "opacity-100" : "opacity-0"}`} />
              ))}
              <div className="absolute bottom-3 left-3 flex gap-1.5 z-10">
                {HERO_IMAGES.map((im, i) => (
                  <button key={im.src} type="button" onClick={() => setHeroIdx(i)} aria-label={`Show ${im.alt}`}
                    className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`} />
                ))}
              </div>
            </div>
            <div className="relative lg:absolute lg:-bottom-6 lg:-right-4 mt-[-2.5rem] lg:mt-0 rounded-2xl bg-white text-slate-900 p-5 shadow-2xl ring-1 ring-black/5 max-w-[19rem] ml-auto">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading font-bold text-base">Daily walkaround check</p>
                  <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mt-0.5">Vehicle · DL19 HCK</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold px-2.5 py-1 shrink-0"><Loader2 size={12} className="animate-spin" /> In progress</span>
              </div>
              <div className="mt-4 space-y-2">
                {WALKAROUND.map((w) => (
                  <div key={w} className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check size={12} /></span>
                    <span className="text-[13px] font-medium text-slate-700">{w}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400 font-semibold border-t border-slate-100 pt-3">
                <span>Scanned · 06:42</span>
                <span>Driver · A. Okafor</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="relative border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-semibold text-slate-400">
            {BADGES.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-400" /> {b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* The real cost of a gap */}
      <section className="max-w-6xl mx-auto px-6 pt-16 sm:pt-20">
        <div className="grid sm:grid-cols-2 gap-5">
          <img src="https://images.unsplash.com/photo-1768400554801-2002b63e0591?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Delivery van" loading="lazy" className="w-full h-56 sm:h-64 object-cover rounded-3xl" />
          <img src="https://images.pexels.com/photos/4620555/pexels-photo-4620555.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Fleet of vans" loading="lazy" className="w-full h-56 sm:h-64 object-cover rounded-3xl" />
        </div>
        <p className="text-center text-sm text-slate-500 mt-4">From vans to HGVs — one system for your whole fleet.</p>
      </section>

      {/* The real cost of a gap */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">The real cost of a gap</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3">One missed check is all it takes.</h2>
          <p className="mt-5 text-slate-500 text-base leading-relaxed">
            A late tacho download. An expired MOT. A defect nobody logged. Any one of these can put your Operator Licence — and your business — in front of a Traffic Commissioner.
          </p>
          <p className="mt-4 text-slate-900 font-semibold">Haulcheck is built to catch the gap before an inspector does.</p>
        </div>
        <div className="space-y-3">
          {RISKS.map((r) => (
            <div key={r} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shrink-0"><AlertTriangle size={12} /> Risk</span>
              <span className="text-sm font-medium text-slate-700">{r}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Platform */}
      <section id="platform" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">Platform</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3">Everything, in one place.</h2>
            </div>
            <Link to="/platform" className="inline-flex items-center gap-2 font-bold text-emerald-600 hover:text-emerald-500">
              See the full platform <ArrowRight size={17} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><f.icon size={20} /></div>
                <h3 className="font-heading font-bold text-base mt-4">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-heading text-4xl sm:text-5xl font-black text-emerald-400">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">Simple pricing</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3">One plan. Everything included.</h2>
            <p className="text-slate-500 mt-4 max-w-md">Start free for 30 days. Keep going for £35 a month or £350 a year (2 months free). Cancel anytime — no contract. More than 10 vehicles? Contact us.</p>
            <ul className="mt-6 space-y-2.5">
              {PLAN_PERKS.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm text-slate-700"><Check size={17} className="text-emerald-500 shrink-0" /> {p}</li>
              ))}
            </ul>
          </div>
          <div data-testid="landing-pricing" className="rounded-3xl bg-slate-900 text-white p-8 shadow-2xl">
            <p className="text-sm font-semibold text-emerald-400">30-day free trial</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-heading text-5xl font-black">£35</span>
              <span className="text-slate-400 mb-1.5">a month</span>
            </div>
            <p className="text-sm text-slate-400 mt-1">or £350 a year — 2 months free</p>
            <Link to={primary} data-testid="landing-start-trial-pricing"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-white px-6 py-3.5 font-bold hover:bg-emerald-400 transition-colors">
              {user ? "Go to dashboard" : "Start your free trial"} <ArrowRight size={18} />
            </Link>
            <p className="text-center text-xs text-slate-500 mt-3">No card needed to start</p>
          </div>
        </div>
      </section>

      {/* Demo request */}
      <section id="demo" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">See it on your fleet</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3">Request a demo.</h2>
            <p className="mt-5 text-slate-500 text-base leading-relaxed">
              Tell us a little about your operation and we'll walk you through Haulcheck on a live call — tailored to your vehicles, drivers and paperwork. The team will be in touch.
            </p>
            <ul className="mt-6 space-y-2.5">
              {["A tour tailored to your fleet size", "See AI document checks in action", "No obligation, no card needed"].map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm text-slate-700"><Check size={17} className="text-emerald-500 shrink-0" /> {p}</li>
              ))}
            </ul>
          </div>
          <form onSubmit={submitDemo} data-testid="demo-form" className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-500">Full name</span>
                <input data-testid="demo-name" value={demo.name} onChange={(e) => setDemo({ ...demo, name: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-500">Company name</span>
                <input data-testid="demo-company" value={demo.company} onChange={(e) => setDemo({ ...demo, company: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-500">Email address</span>
                <input data-testid="demo-email" type="email" value={demo.email} onChange={(e) => setDemo({ ...demo, email: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-500">Phone number</span>
                <input data-testid="demo-phone" value={demo.phone} onChange={(e) => setDemo({ ...demo, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
              </label>
            </div>
            <label className="block">
              <span className="text-xs font-semibold text-slate-500">Approximate fleet size</span>
              <select data-testid="demo-fleet-size" value={demo.fleet_size} onChange={(e) => setDemo({ ...demo, fleet_size: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400">
                <option value="">Select fleet size…</option>
                {FLEET_SIZES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <button data-testid="demo-submit" type="submit" disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white px-6 py-3.5 font-bold hover:bg-slate-800 transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Request my demo"} <ArrowRight size={18} />
            </button>
            <p className="text-center text-xs text-slate-400">We'll be in touch soon</p>
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-semibold">Non-compliance is expensive. Prevention isn't.</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight max-w-2xl mx-auto mt-3">Get your fleet audit-ready.</h2>
        <p className="text-slate-500 mt-4 max-w-xl mx-auto">Start a free trial and see every document, vehicle, driver and defect in one place — before your next inspection.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to={primary} data-testid="landing-start-trial-final"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-white px-7 py-3.5 font-bold hover:bg-emerald-400 transition-colors">
            {user ? "Go to dashboard" : "Start your free trial"} <ArrowRight size={18} />
          </Link>
          <a href="#demo" className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-7 py-3.5 font-semibold text-slate-700 hover:border-slate-400 transition-colors">
            Book a walkthrough
          </a>
        </div>
        <p className="mt-4 text-xs text-slate-400">No card required · Cancel anytime</p>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 text-white">
              <span className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center font-heading font-black text-sm">HC</span>
              <span className="font-heading font-black tracking-tight">Haulcheck</span>
            </div>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-3">Platform</p>
            <ul className="space-y-2 text-sm">
              <li><Link to={primary} className="hover:text-white">Documents</Link></li>
              <li><Link to={primary} className="hover:text-white">Vehicles</Link></li>
              <li><Link to={primary} className="hover:text-white">Drivers</Link></li>
              <li><Link to={primary} className="hover:text-white">Maintenance</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-3">Company</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">Our story</Link></li>
              <li><Link to="/about" className="hover:text-white">Security &amp; GDPR</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              <li><a href="/driver" className="hover:text-white flex items-center gap-1.5"><MapPin size={13} /> Driver app</a></li>
            </ul>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-3">Legal</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="hover:text-white">Privacy &amp; GDPR</Link></li>
              <li><Link to="/dpa" className="hover:text-white">DPA</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Haulcheck. All rights reserved. · RSA · DVSA · EU · GDPR · DPA
        </div>
      </footer>
    </div>
  );
}
